"use client";

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { useSession } from "next-auth/react";
import { FavoriteService } from "@/lib/services/favoriteService";

interface FavoritesContextType {
  favoriteIds: Set<string>;
  isFavorite: (cardId: string) => boolean;
  toggleFavorite: (cardId: string) => Promise<boolean>;
  isLoading: boolean;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

export function FavoritesProvider({
  children,
  initialFavoriteCardIds = []
}: {
  children: ReactNode;
  initialFavoriteCardIds?: string[];
}) {
  const { status } = useSession();
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(
    () => new Set(initialFavoriteCardIds)
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Fetch favorites on client side when user is authenticated
  const fetchFavorites = useCallback(async () => {
    try {
      setIsLoading(true);
      const ids = await FavoriteService.getUserFavoriteCardIds();
      setFavoriteIds(new Set(ids));
    } catch (error) {
      console.error("Error fetching favorites:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (status === "authenticated") {
      fetchFavorites();
    } else if (status === "unauthenticated") {
      setFavoriteIds(new Set());
    }
  }, [status, fetchFavorites]);

  const isFavorite = useCallback(
    (cardId: string) => favoriteIds.has(cardId),
    [favoriteIds]
  );

  const toggleFavorite = useCallback(
    async (cardId: string): Promise<boolean> => {
      const wasFavorite = favoriteIds.has(cardId);
      
      // Optimistic state update
      setFavoriteIds((prev) => {
        const next = new Set(prev);
        if (wasFavorite) {
          next.delete(cardId);
        } else {
          next.add(cardId);
        }
        return next;
      });

      try {
        const res = await FavoriteService.toggleFavoriteCard(cardId);
        if (!res.response_status) {
          // Revert optimistic update on failure
          setFavoriteIds((prev) => {
            const next = new Set(prev);
            if (wasFavorite) {
              next.add(cardId);
            } else {
              next.delete(cardId);
            }
            return next;
          });
          return false;
        }
        return true;
      } catch (error) {
        // Revert optimistic update on error
        setFavoriteIds((prev) => {
          const next = new Set(prev);
          if (wasFavorite) {
            next.add(cardId);
          } else {
            next.delete(cardId);
          }
          return next;
        });
        return false;
      }
    },
    [favoriteIds]
  );

  return (
    <FavoritesContext.Provider
      value={{
        favoriteIds,
        isFavorite,
        toggleFavorite,
        isLoading
      }}
    >
      {children}
    </FavoritesContext.Provider>
  );
}

const fallbackFavoritesContext: FavoritesContextType = {
  favoriteIds: new Set(),
  isFavorite: () => false,
  toggleFavorite: async () => false,
  isLoading: false,
};

export function useFavorites() {
  const context = useContext(FavoritesContext);
  if (!context) {
    return fallbackFavoritesContext;
  }
  return context;
}

